import { useState } from 'react';
import type { AssessmentQuestion } from '@/lib/types';

interface ClassificationProps {
  question: AssessmentQuestion;
  onAnswer: (answer: string[]) => void;
  disabled?: boolean;
  currentAnswer?: string[];
}

export function Classification({
  question,
  onAnswer,
  disabled = false,
  currentAnswer = [],
}: ClassificationProps) {
  const [selected, setSelected] = useState<string[]>(currentAnswer);

  const handleToggle = (option: string) => {
    if (disabled) return;

    const newSelected = selected.includes(option)
      ? selected.filter((s) => s !== option)
      : [...selected, option];

    setSelected(newSelected);
    onAnswer(newSelected);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
        {question.question}
      </h3>

      <p className="text-sm text-slate-600 dark:text-slate-400">
        Select all that apply
      </p>

      <div className="space-y-2">
        {question.options?.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleToggle(option)}
            disabled={disabled}
            className={`
              w-full text-left px-4 py-3 rounded-lg border-2 transition-all
              ${
                selected.includes(option)
                  ? 'accent-border accent-bg-medium accent-text-dark'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center gap-3">
              <div
                className={`
                  w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                  ${
                    selected.includes(option)
                      ? 'accent-border accent-bg'
                      : 'border-slate-300 dark:border-slate-600'
                  }
                `}
              >
                {selected.includes(option) && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm">{option}</span>
            </div>
          </button>
        ))}
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        {selected.length} selected
      </p>
    </div>
  );
}
